// Runtime DOM target resolver. Instead of a hand-maintained registry, this finds
// the element that best matches a free-text query by scanning the LIVE page —
// headings, sections, cards, labelled elements and image alts. Because it reads
// whatever is currently rendered, it automatically stays in sync as the site
// changes, and can pinpoint individual items (a service, a product card, a
// team member), not just whole sections.

const HIGHLIGHT_CLASS = 'assistant-highlight'
const HIGHLIGHT_MS = 2800

const STOPWORDS = new Set([
  'the', 'a', 'an', 'to', 'of', 'and', 'or', 'for', 'on', 'in', 'me', 'us', 'our', 'your',
  'show', 'see', 'go', 'open', 'take', 'scroll', 'view', 'where', 'is', 'are', 'section',
  'page', 'part', 'about', 'please', 'can', 'you', 'i', 'want', 'find', 'point', 'out', 'it',
])

function tokenize(s: string): string[] {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter((t) => t && !STOPWORDS.has(t))
}

interface Candidate {
  el: HTMLElement
  label: string
}

/** Climb to a sensible container to highlight (a section/card), not a bare heading. */
function containerFor(el: HTMLElement): HTMLElement {
  const container = el.closest(
    'section, article, [data-section], [class*="card"], [class*="block"], [class*="section"], li',
  )
  return (container as HTMLElement) ?? el
}

function collectCandidates(): Candidate[] {
  const root = (document.querySelector('main') as HTMLElement) ?? document.body
  const out: Candidate[] = []
  const seen = new Set<HTMLElement>()

  const add = (el: HTMLElement | null, label: string) => {
    if (!el || !label.trim()) return
    const text = label.trim().slice(0, 120)
    out.push({ el, label: text })
  }

  // Headings represent their surrounding section/card.
  root.querySelectorAll<HTMLElement>('h1, h2, h3, h4').forEach((h) => {
    add(containerFor(h), h.textContent ?? '')
  })

  // Explicitly labelled / identified elements.
  root.querySelectorAll<HTMLElement>('[data-section], [aria-label], [id]').forEach((e) => {
    const raw = e.getAttribute('data-section') || e.getAttribute('aria-label') || e.id
    if (raw) add(e, raw.replace(/[-_]/g, ' '))
  })

  // Image alts (product/project cards, logos).
  root.querySelectorAll<HTMLImageElement>('img[alt]').forEach((img) => {
    const alt = img.getAttribute('alt') || ''
    if (alt) add(containerFor(img), alt)
  })

  // Buttons/links with clear text (e.g. "Apply", "Contact us").
  root.querySelectorAll<HTMLElement>('a, button').forEach((a) => {
    const t = a.textContent?.trim() ?? ''
    if (t && t.length <= 40) add(a, t)
  })

  // De-duplicate by element, keeping the first (usually the most specific label).
  return out.filter((c) => {
    if (seen.has(c.el)) return false
    seen.add(c.el)
    return true
  })
}

function score(queryTokens: string[], label: string): number {
  const labelTokens = tokenize(label)
  if (labelTokens.length === 0 || queryTokens.length === 0) return 0

  const labelSet = new Set(labelTokens)
  let hits = 0
  for (const t of queryTokens) if (labelSet.has(t)) hits++

  const coverage = hits / queryTokens.length // how much of the query is matched
  const labelQuery = label.toLowerCase()
  const queryStr = queryTokens.join(' ')

  let s = coverage
  if (labelQuery.includes(queryStr)) s += 0.4 // strong phrase containment
  // Prefer concise labels (a card title) over long paragraphs.
  if (labelTokens.length <= 8) s += 0.1
  return s
}

/** Find the element that best matches `query` on the current page, or null. */
export function findBestMatch(query: string): HTMLElement | null {
  const queryTokens = tokenize(query)
  if (queryTokens.length === 0) return null

  let best: { el: HTMLElement; s: number } | null = null
  for (const c of collectCandidates()) {
    const s = score(queryTokens, c.label)
    if (s > 0 && (!best || s > best.s)) best = { el: c.el, s }
  }
  // Require a reasonable match so we don't highlight something random.
  return best && best.s >= 0.5 ? best.el : null
}

function pulse(el: Element) {
  el.scrollIntoView({ behavior: 'smooth', block: 'center' })
  el.classList.remove(HIGHLIGHT_CLASS)
  void (el as HTMLElement).offsetWidth // restart animation
  el.classList.add(HIGHLIGHT_CLASS)
  window.setTimeout(() => el.classList.remove(HIGHLIGHT_CLASS), HIGHLIGHT_MS)
}

/** Resolve `query` against the live DOM and highlight the best match. */
export function resolveAndHighlight(query: string): boolean {
  const el = findBestMatch(query)
  if (!el) return false
  pulse(el)
  window.setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'center' }), 350)
  return true
}
